import { ChangeDetectionStrategy, Component, DestroyRef, computed, forwardRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { debounceTime } from "rxjs";
import { InputComponent, PaginatorComponent, PaginatorVariant } from "@sxm-content-creators/tools-ds";
import { TagListComponent } from "../../../../components/tag/tag-list.component";
import { TagOption } from "../../../../components/tag/tag.component";
import { TenantsApiService } from "../../../../shared/services/tenants.api.service";
import { TenantResponse } from "../../../tenants/models/tenant.model";

@Component({
    selector: "app-tenant-access",
    templateUrl: "./tenant-access.component.html",
    styleUrl: "./tenant-access.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, InputComponent, PaginatorComponent, TagListComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TenantAccessComponent),
            multi: true
        }
    ]
})
export class TenantAccessComponent implements ControlValueAccessor {
    protected TENANT_PAGE_SIZE = 10;

    private readonly tenantsApi = inject(TenantsApiService);
    private readonly destroyRef = inject(DestroyRef);

    readonly searchControl = new FormControl("", { nonNullable: true });

    private readonly allTenants = signal<TenantResponse[]>([]);
    readonly isLoading = signal(true);
    readonly searchTerm = signal("");
    readonly pageIndex = signal(0);
    private readonly selectedIds = signal<string[]>([]);

    private readonly selectedIdSet = computed(() => new Set(this.selectedIds()));

    readonly filteredTenants = computed(() => {
        const term = this.searchTerm().toLowerCase();
        const tenants = this.allTenants();
        if (!term) {
            return tenants;
        }
        return tenants.filter(tenant => {
            const name = tenant.name?.toLowerCase() ?? "";
            const aliases = tenant.searchAliases ?? [];
            return name.includes(term) || aliases.some(alias => alias.toLowerCase().includes(term));
        });
    });

    protected readonly pageSize = this.TENANT_PAGE_SIZE;
    protected readonly paginatorVariant = PaginatorVariant.CURRENT_AND_TOTAL;

    readonly pagedTenants = computed(() => {
        const start = this.pageIndex() * this.TENANT_PAGE_SIZE;
        return this.filteredTenants().slice(start, start + this.TENANT_PAGE_SIZE);
    });

    readonly tags = computed<TagOption<string>[]>(() => {
        const selected = this.selectedIdSet();
        return this.allTenants()
            .filter(tenant => selected.has(tenant.id))
            .map(tenant => ({ value: tenant.id, label: tenant.name }));
    });

    readonly showNoResults = computed(() => !this.isLoading() && this.filteredTenants().length === 0);

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    constructor() {
        this.tenantsApi
            .getAuthorizedTenants()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: tenants => {
                    this.allTenants.set(tenants);
                    this.isLoading.set(false);
                },
                error: () => {
                    this.allTenants.set([]);
                    this.isLoading.set(false);
                }
            });

        this.searchControl.valueChanges
            .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.searchTerm.set(value.trim());
                this.pageIndex.set(0);
            });
    }

    isSelected(tenant: TenantResponse): boolean {
        return this.selectedIdSet().has(tenant.id);
    }

    addTenant(tenant: TenantResponse): void {
        if (this.isSelected(tenant)) {
            return;
        }
        this.commitSelection([...this.selectedIds(), tenant.id]);
    }

    protected onTagRemoved(tag: TagOption): void {
        this.commitSelection(this.selectedIds().filter(id => id !== tag.value));
    }

    private commitSelection(ids: string[]): void {
        this.selectedIds.set(ids);
        this.onChange(ids);
        this.onTouched();
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex.set(event.pageIndex);
    }

    writeValue(value: string[] | null): void {
        this.selectedIds.set(value ?? []);
    }

    registerOnChange(fn: (value: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
}
